import { add, format } from "date-fns";
import * as Yup from "yup";
import httpClient from "../config/HttpClient";
import EntityId from "../EntityId";
import { InvoiceEmailModel } from "./InvoiceEmailModel";

export interface ProformaInvoiceData {
  date: string;
  price: number | null;
  periodFrom: string;
  periodTo: string;
  jobTitle: string | null;
  product: string | null;
  productPeriod: string | null;
}

export const getDefaultProformaInvoiceData = (): ProformaInvoiceData => {
  const now = new Date();
  const oneYearLater = add(now, { years: 1 });
  const dateStr = format(now, "yyyy-MM-dd");
  const endDateStr = format(oneYearLater, "yyyy-MM-dd");

  return {
    date: dateStr,
    price: null,
    periodFrom: dateStr,
    periodTo: endDateStr,
    jobTitle: null,
    product: "Leidinio „Teisės aktų žinios“ prenumerata",
    productPeriod: `${dateStr} - ${endDateStr}`,
  };
};

export const proformaInvoiceSchema = Yup.object().shape({
  date: Yup.date().required("Data yra privaloma"),
  periodFrom: Yup.date().required("Prenumeratos pradžios data yra privaloma"),
  periodTo: Yup.date().required("Prenumeratos pabaigos data yra privaloma"),
  price: Yup.number()
    .typeError("Kaina turi būti validus skaičius")
    .required("Kaina yra privaloma"),
});

export class ProformaInvoiceModel implements ProformaInvoiceData {
  date!: string;
  price: number | null = null;
  periodFrom!: string;
  periodTo!: string;
  jobTitle: string | null = null;
  product: string | null = null;
  productPeriod: string | null = null;

  constructor(data: Partial<ProformaInvoiceData> = {}) {
    const defaults = getDefaultProformaInvoiceData();
    Object.assign(this, { ...defaults, ...data });
  }

  static createWithDates(periodFrom: string, periodTo: string): ProformaInvoiceData {
    return {
      ...getDefaultProformaInvoiceData(),
      periodFrom,
      periodTo,
      productPeriod: `${periodFrom} - ${periodTo}`,
    };
  }

  static async save(model: ProformaInvoiceData, clientId: string): Promise<EntityId> {
    const response = await httpClient.post<EntityId>(
      `/clients/${clientId}/proforma-invoices`,
      model
    );
    return response.data;
  }

  static async getLastProformaSentDate(clientId: string): Promise<string> {
    const response = await httpClient.get<string>(
      `/clients/${clientId}/proforma-invoices/last-sent`
    );
    return response.data;
  }

  static async getPdf(clientId: string): Promise<string> {
    const response = await httpClient.get(
      `/clients/${clientId}/proforma-invoices/pdf`,
      { responseType: "blob" }
    );
    return URL.createObjectURL(response.data);
  }

  static async send(content: InvoiceEmailModel, clientId: string): Promise<string> {
    const response = await httpClient.post<EntityId>(
      `/clients/${clientId}/proforma-invoices/send`,
      content
    );
    return response.data.id;
  }

  static async getPdfPreview(model: ProformaInvoiceData, clientId: string): Promise<string> {
    const response = await httpClient.post(
      `/clients/${clientId}/proforma-invoices/preview-pdf`,
      model,
      { responseType: "blob" }
    );
    return URL.createObjectURL(response.data);
  }
}

export default ProformaInvoiceModel;
